import axios from 'axios';
import { API_URL } from '@/config/api.config';

// Helper function to ensure authorization header is included
const authorizedRequest = async (requestFn) => {
  const token = localStorage.getItem('token');
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  
  try {
    return await requestFn(headers);
  } catch (error) {
    console.error('API Error:', error);
    
    // Handle unauthorized (401) errors
    if (error.response && error.response.status === 401) {
      console.error('Authentication error - please log in again');
      // Redirect to login could be handled here if needed
    }
    throw error;
  }
};

// Teacher request APIs
export const fetchRequestTypes = async () => {
  return authorizedRequest(async (headers) => {
    const response = await axios.get(`${API_URL}/request-types`, { headers });
    const teacherTypes = response.data.filter(type => 
      [19, 20, 21, 22].includes(type.id) // IDs for teacher-specific request types
    );
    return teacherTypes.map(type => ({
      name: type.type,
      id: type.id
    }));
  });
};

export const fetchGradeTypes = async () => {
  return authorizedRequest(async (headers) => {
    const response = await axios.get(`${API_URL}/grade-types`, { headers });
    return response.data;
  });
};

export const createTeacherRequest = async (data) => {
  return authorizedRequest(async (headers) => {
    // Transform the data into the expected structure
    const requestData = {
      request_type_id: data.request_type_id || data.cause_id,
      ciclu_de_studii: data.ciclu_de_studii,
      facultatea: data.facultatea,
      frecventa: data.frecventa,
      specialitatea: data.specialitatea,
      grupa: data.grupa,
      semestru: data.semestru,
      disciplina: data.disciplina,
      explicatia: data.explicatia,
      // Transform grade changes if needed
      grade_changes: data.gradeChanges ? data.gradeChanges.map(change => ({
        student_name: change.student_name,
        disciplina_name: change.disciplina_name || data.disciplina,
        grade_type_id: typeof change.grade_type === 'string' ? getGradeTypeIdByName(change.grade_type) : change.grade_type_id,
        old_value: change.old_value || '',
        new_value: change.new_value
      })) : data.grade_changes
    };

    const response = await axios.post(`${API_URL}/teacher-requests`, requestData, { headers });
    return response.data;
  });
};

// Helper function to convert grade type name to ID
const getGradeTypeIdByName = (typeName) => {
  const typeMap = {
    'Examen final': 4,
    'Test intermediar': 1,
    'Activitate de laborator': 2,
    'Prezență și activitate': 2,
    'Lucru individual': 2,
    'Colocviu': 5,
    'Practica': 6
  };
  return typeMap[typeName] || 4; // Default to exam grade if not found
};

export const updateTeacherRequest = async (id, data) => {
  return authorizedRequest(async (headers) => {
    const response = await axios.patch(`${API_URL}/teacher-requests/${id}`, data, { headers });
    return response.data;
  });
};

export const deleteTeacherRequest = async (id) => {
  return authorizedRequest(async (headers) => {
    const response = await axios.delete(`${API_URL}/teacher-requests/${id}`, { headers });
    return response.data;
  });
};

export const getTeacherRequestById = async (id) => {
  return authorizedRequest(async (headers) => {
    const response = await axios.get(`${API_URL}/teacher-requests/${id}/teacher-request-details`, { headers });
    return response.data;
  });
};

export const getAllTeacherRequests = async () => {
  return authorizedRequest(async (headers) => {
    const response = await axios.get(`${API_URL}/teacher-requests/requests-table-data`, { headers });
    return response.data;
  });
};

export const getTeacherRequestsByUserId = async (userId) => {
  return authorizedRequest(async (headers) => {
    const response = await axios.get(`${API_URL}/teacher-requests/${userId}/user-requests-table-data`, { headers });
    return response.data;
  });
};

export const getTeacherRequestsByDepartment = async (department) => {
  return authorizedRequest(async (headers) => {
    // This assumes there's an endpoint for filtering requests by department
    const response = await axios.get(`${API_URL}/teacher-requests/department/${encodeURIComponent(department)}`, { headers });
    return response.data;
  });
};

export const getTeacherRequestCommentsHistory = async (requestId) => {
  return authorizedRequest(async (headers) => {
    const response = await axios.get(`${API_URL}/teacher-requests/${requestId}/comments-history`, { headers });
    return response.data;
  });
};

export const getBulkTeacherRequestCommentsHistory = async (requestIds) => {
  return authorizedRequest(async (headers) => {
    const response = await axios.post(`${API_URL}/teacher-responses/bulk-comments-history`, { requestIds }, { headers });
    return response.data;
  });
};

// Teacher approval APIs
export const getTeacherApprovalData = async () => {
  return authorizedRequest(async (headers) => {
    const response = await axios.get(`${API_URL}/teacher-responses/approval-data`, { headers });
    return response.data;
  });
};

export const getTeacherApprovalDataByType = async (typeId) => {
  return authorizedRequest(async (headers) => {
    // If no typeId is provided, use a default (13 - Emis de profesor)
    const actualTypeId = typeId || 13;
    const response = await axios.get(`${API_URL}/teacher-responses/${actualTypeId}/teacher-approval-data`, { headers });
    return response.data;
  });
};

export const getTeacherApprovalDataByUserId = async (userId) => {
  return authorizedRequest(async (headers) => {
    const response = await axios.get(`${API_URL}/teacher-responses/user/${userId}/approval-data`, { headers });
    return response.data;
  });
};

export const getTeacherApprovalDataByTypeAndDepartment = async (typeId, department) => {
  return authorizedRequest(async (headers) => {
    // If no typeId is provided, use a default (13 - Emis de profesor)
    const actualTypeId = typeId || 13;
    const response = await axios.get(`${API_URL}/teacher-responses/${actualTypeId}/teacher-approval-user-data?curentDepartment=${encodeURIComponent(department)}`, { headers });
    return response.data;
  });
};

/**
 * Check if a user is a dean for a specific faculty
 * @param {number} userId - The user ID
 * @param {string} facultatea - The faculty name
 * @returns {Promise<boolean>} - Whether the user is dean for this faculty
 */
export const checkIsDean = async (userId, facultatea) => {
  return authorizedRequest(async (headers) => {
    const response = await axios.get(
      `${API_URL}/teacher-requests/${userId}/check-is-dean?facultatea=${encodeURIComponent(facultatea)}`, 
      { headers }
    );
    return response.data;
  });
};

export async function saveTeacherResponse(data) {
  return authorizedRequest(async (headers) => {
    const response = await axios.post(`${API_URL}/teacher-responses`, data, { headers });
    return response.data;
  });
}

export const downloadTeacherReport = async (params, reportType = 'detailed') => {
  if (!params.startDate || !params.endDate) {
    throw new Error('Start date and end date are required');
  }
  
  return authorizedRequest(async (headers) => {
    const requestParams = { ...params, reportType };
    
    try {
      const response = await axios.get(`${API_URL}/teacher-requests/report`, {
        params: requestParams,
        headers: {
          ...headers,
          Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        },
        responseType: 'blob',
        validateStatus: (status) => {
          return status >= 200 && status < 300;
        }
      });

      const contentType = response.headers['content-type'];
      if (!contentType || !contentType.includes('spreadsheet')) {
        console.error('Unexpected content type received:', contentType);
        throw new Error('Server did not return an Excel file');
      }

      return response;
    } catch (error) {
      console.error('Error in downloadTeacherReport:', error);
      if (error.response?.data instanceof Blob) {
        const text = await error.response.data.text();
        try {
          const errorJson = JSON.parse(text);
          error.message = errorJson.message || error.message;
        } catch (e) {
          console.error('Error parsing error response:', e);
        }
      }
      throw error;
    }
  });
};

/**
 * Fetches the list of groups from the server proxy to avoid CORS issues
 * @returns {Promise<Array>} Array of objects with group information
 */
export const fetchGroups = async () => {
  return authorizedRequest(async (headers) => {
    const response = await axios.post(`${API_URL}/teacher-requests/groups`, {}, { headers });
    return response.data || [];
  });
};

/**
 * Fetch group details from external API by group name
 * @param {string} groupName - The group name (e.g., "IT21Z")
 * @returns {Promise<Object>} Group details object
 */
export const fetchGroupDetails = async (groupName) => {
  if (!groupName || groupName.length < 2) {
    return null;
  }
    try {
    const response = await axios.get(`/group/name/${groupName}/detailed`, {
      timeout: 5000
    });
    
    const data = response.data;
    if (data) {
      if (data.TipStudii === 'Licen?a') {
        data.CicluDeStudii = 'Licența';
      }
      if (data.TipStudii === 'Master') {
        data.CicluDeStudii = 'Master';
      }
    }
    
    return data;
  } catch (error) {
    console.error('Error fetching group details:', error);
    return null;
  }
};

/**
 * Fetch disciplines for a group and semester from external API
 * @param {string} groupName - The group name (e.g., "IT21Z")
 * @param {number} semester - The semester number
 * @returns {Promise<Array>} Array of discipline objects
 */
export const fetchGroupDisciplines = async (groupName, semester) => {
  if (!groupName || !semester) {
    return [];
  }
    try {
    const response = await axios.get(`/group/name/${groupName}/disciplines/semester/${semester}`, {
      timeout: 5000 // 5 second timeout
    });
    
    return response.data || [];
  } catch (error) {
    console.error('Error fetching group disciplines:', error);
    return [];
  }
};


